import {useEffect, useRef, useState} from 'react'

export type RetainedMotionState = 'open' | 'closed'

/** `appear: false` renders a surface that is open on mount without its enter transition. */
export const useRetainedMotionState = (
    open: boolean,
    exitDurationMs = 200,
    appear = true
): {shouldRender: boolean; motionState: RetainedMotionState} => {
    const [shouldRender, setShouldRender] = useState(open)
    const [motionState, setMotionState] = useState<RetainedMotionState>(open && !appear ? 'open' : 'closed')
    const shouldRenderRef = useRef(open)
    // Survives Effect replay so StrictMode does not replay the skipped enter transition.
    const skipEnterRef = useRef(open && !appear)
    const animationFrameRef = useRef<number | null>(null)
    const exitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        const cancelScheduledCallbacks = () => {
            if (animationFrameRef.current !== null) {
                window.cancelAnimationFrame(animationFrameRef.current)
                animationFrameRef.current = null
            }

            if (exitTimeoutRef.current !== null) {
                clearTimeout(exitTimeoutRef.current)
                exitTimeoutRef.current = null
            }
        }

        cancelScheduledCallbacks()

        if (open && skipEnterRef.current) return cancelScheduledCallbacks

        if (open) {
            shouldRenderRef.current = true
            setShouldRender(true)
            setMotionState('closed')
            // Two frames: the browser must paint the closed styles once, or the enter transition never starts.
            animationFrameRef.current = window.requestAnimationFrame(() => {
                animationFrameRef.current = window.requestAnimationFrame(() => {
                    animationFrameRef.current = null
                    setMotionState('open')
                })
            })
        } else {
            skipEnterRef.current = false
            setMotionState('closed')

            if (shouldRenderRef.current) {
                exitTimeoutRef.current = setTimeout(() => {
                    exitTimeoutRef.current = null
                    shouldRenderRef.current = false
                    setShouldRender(false)
                }, exitDurationMs)
            }
        }

        return cancelScheduledCallbacks
    }, [exitDurationMs, open])

    return {shouldRender, motionState}
}
